import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronRight, Plus, Key, Building2, Wallet, Settings, Trash2, Edit2, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';
import { MOCK_PROVIDERS, formatCurrency, getInitials, getProviderTotals } from '../data/providers';
export function ProviderDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [activeEnv, setActiveEnv] = useState('Produzione');
  const provider = MOCK_PROVIDERS.find(p => p.id === id);
  if (!provider) {
    return <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8 flex items-center justify-center flex-col" data-id="element-4094">
        <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-4095">
          Provider non trovato
        </h2>
        <button onClick={() => navigate('/providers')} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors" data-id="element-4096">
          <ArrowLeft className="w-4 h-4" data-id="element-4097" />
          Torna ai Provider
        </button>
      </div>;
  }
  const {
    totalBudget,
    totalSpent,
    percent
  } = getProviderTotals(provider);
  return <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8" data-id="element-4098">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-2 mb-6 text-[13px]" data-id="element-4099">
        <Link to="/providers" className="text-gray-500 hover:text-[#1A202C] transition-colors font-medium" data-id="element-4100">
          Provider
        </Link>
        <ChevronRight className="w-3.5 h-3.5 text-gray-400" data-id="element-4101" />
        <span className="text-[#1A202C] font-semibold" data-id="element-4102">{provider.name}</span>
      </nav>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col" data-id="element-4103">
        {/* Header */}
        <div className="px-6 py-6 border-b border-gray-100 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-id="element-4104">
          <div className="flex items-center gap-4" data-id="element-4105">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center border border-gray-100 shadow-sm bg-white overflow-hidden flex-shrink-0" style={{
            backgroundColor: provider.logo ? 'white' : `${provider.color}15`
          }} data-id="element-4106">
              {provider.logo ? <img src={provider.logo} alt={provider.name} className="w-10 h-10 object-contain" data-id="element-4107" /> : <span className="text-[20px] font-bold" style={{
              color: provider.color
            }} data-id="element-4108">
                  {getInitials(provider.name)}
                </span>}
            </div>
            <div data-id="element-4109">
              <div className="flex items-center gap-3 mb-1.5" data-id="element-4110">
                <h1 className="text-[22px] font-heading font-semibold text-[#1A202C] tracking-tight" data-id="element-4111">
                  {provider.name}
                </h1>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${provider.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-600 border border-gray-200'}`} data-id="element-4112">
                  {provider.status === 'Active' ? <CheckCircle2 className="w-3.5 h-3.5" data-id="element-4113" /> : <XCircle className="w-3.5 h-3.5" data-id="element-4114" />}
                  {provider.status === 'Active' ? 'Attivo' : 'Inattivo'}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-4115">
                {provider.models.map(model => <span key={model} className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-gray-50 text-gray-600 border border-gray-200" data-id="element-4116">
                    {model}
                  </span>)}
              </div>
            </div>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-[#1A202C] hover:bg-gray-100 rounded-lg transition-colors text-[13px] font-medium border border-gray-200 shadow-sm" data-id="element-4117">
            <Settings className="w-4 h-4" data-id="element-4118" />
            Impostazioni Provider
          </button>
        </div>

        {/* Summary Stats */}
        <div className="px-6 py-5 border-b border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-50/50" data-id="element-4119">
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm" data-id="element-4120">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 font-medium" data-id="element-4121">
              Budget Totale
            </p>
            <p className="text-[24px] font-semibold text-[#1A202C]" data-id="element-4122">
              {formatCurrency(totalBudget)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm" data-id="element-4123">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 font-medium" data-id="element-4124">
              Spesa Corrente
            </p>
            <p className="text-[24px] font-semibold text-[#1A202C]" data-id="element-4125">
              {formatCurrency(totalSpent)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm" data-id="element-4126">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 font-medium" data-id="element-4127">
              Utilizzo Budget
            </p>
            <div className="flex items-end gap-3" data-id="element-4128">
              <p className={`text-[24px] font-semibold leading-none ${percent >= 80 ? 'text-amber-600' : 'text-[#1A202C]'}`} data-id="element-4129">
                {percent}%
              </p>
              <div className="w-full bg-gray-100 rounded-full h-2 mb-1.5 flex-1" data-id="element-4130">
                <div className={`h-2 rounded-full transition-all ${percent >= 80 ? 'bg-amber-500' : 'bg-[#00B4D8]'}`} style={{
                width: `${percent}%`
              }} data-id="element-4131" />
              </div>
            </div>
          </div>
        </div>

        {/* Environment Tabs + Budget Lines */}
        <div className="px-6 py-6 flex-1" data-id="element-4132">
          {/* Environment Tabs */}
          <div className="border-b-2 border-gray-200 mb-6" data-id="element-4133">
            <div className="-mb-[2px] flex items-center gap-6 overflow-x-auto custom-scrollbar" data-id="element-4134">
              {['Produzione', 'Staging', 'Development'].map(env => <button key={env} onClick={() => setActiveEnv(env)} className={`pb-3 text-[14px] font-medium transition-colors whitespace-nowrap border-b-2 ${activeEnv === env ? 'border-[#00B4D8] text-[#2A3B5C]' : 'border-transparent text-gray-500 hover:text-gray-800'}`} data-id="element-4135">
                  {env}
                </button>)}
            </div>
          </div>

          <div className="flex items-center justify-between mb-6" data-id="element-4136">
            <h3 className="text-[14px] font-semibold text-[#1A202C] uppercase tracking-wider" data-id="element-4137">
              Linee di Budget — {activeEnv} ({provider.budgetLines.length})
            </h3>
            <button className="flex items-center gap-2 px-4 py-2 bg-[#2A3B5C] text-white rounded-lg hover:bg-[#1E2A40] transition-colors shadow-sm" data-id="element-4138">
              <Plus className="w-4 h-4" data-id="element-4139" />
              <span className="text-[13px] font-medium" data-id="element-4140">Aggiungi Linea</span>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-4141">
            {provider.budgetLines.map(line => {
            const percentSpent = Math.min(100, Math.round(line.spent / line.budget * 100));
            const isNearLimit = percentSpent >= 80;
            return <div key={line.id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow" data-id="element-4142">
                  <div className="flex items-start justify-between mb-4" data-id="element-4143">
                    <div data-id="element-4144">
                      <div className="flex items-center gap-2 mb-1.5" data-id="element-4145">
                        <h4 className="text-[15px] font-semibold text-[#1A202C]" data-id="element-4146">
                          {line.name}
                        </h4>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${line.type === 'Interno' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-purple-50 text-purple-700 border border-purple-200'}`} data-id="element-4147">
                          {line.type}
                        </span>
                      </div>
                      {line.fornitore && <p className="text-[12px] text-gray-500 flex items-center gap-1.5" data-id="element-4148">
                          <Building2 className="w-3.5 h-3.5" data-id="element-4149" />
                          Fornitore: {line.fornitore}
                        </p>}
                    </div>
                    <div className="flex items-center gap-1" data-id="element-4150">
                      <button title="Modifica" className="p-1.5 text-gray-400 hover:text-[#00B4D8] hover:bg-gray-50 rounded-md transition-colors" data-id="element-4151">
                        <Edit2 className="w-4 h-4" data-id="element-4152" />
                      </button>
                      <button title="Elimina" className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-50 rounded-md transition-colors" data-id="element-4153">
                        <Trash2 className="w-4 h-4" data-id="element-4154" />
                      </button>
                    </div>
                  </div>

                  {/* API Key */}
                  <div className="flex items-center gap-2 mb-5 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-fit" data-id="element-4155">
                    <Key className="w-4 h-4 text-gray-400" data-id="element-4156" />
                    <span className="text-[12px] font-mono text-gray-600" data-id="element-4157">
                      {line.apiKeyMasked}
                    </span>
                  </div>

                  {/* Budget Progress */}
                  <div data-id="element-4158">
                    <div className="flex items-center justify-between text-[12px] mb-2" data-id="element-4159">
                      <span className="text-gray-500 flex items-center gap-1.5 font-medium" data-id="element-4160">
                        <Wallet className="w-3.5 h-3.5" data-id="element-4161" />
                        Spesa mensile
                      </span>
                      <span className="font-semibold text-[#1A202C]" data-id="element-4162">
                        {formatCurrency(line.spent)} /{' '}
                        {formatCurrency(line.budget)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-4163">
                      <div className={`h-2 rounded-full transition-all ${isNearLimit ? 'bg-amber-500' : 'bg-[#00B4D8]'}`} style={{
                    width: `${percentSpent}%`
                  }} data-id="element-4164" />
                    </div>
                    <div className="flex justify-end mt-1.5" data-id="element-4165">
                      <span className={`text-[11px] font-medium ${isNearLimit ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-4166">
                        {percentSpent}% utilizzato
                      </span>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </div>;
}